/**
 * Cart delivery frequency / Stay.ai consent flow for JOI mini-cart.
 * Matches frequencies by plan NAME across eligible lines, then applies
 * native Shopify selling_plan via /cart/change.js.
 */
(function (global, $) {
  'use strict';

  var ROOT_SEL = '.mini-cart-content';
  var CATALOG_URL = (global.Shopify && Shopify.routes && Shopify.routes.root
    ? Shopify.routes.root
    : '/') + 'cart?view=selling-plans';

  function hasNativeSellingPlan(item) {
    if (!item) return false;
    if (item.selling_plan_allocation && item.selling_plan_allocation.selling_plan) return true;
    if (item.selling_plan) return true;
    return false;
  }

  // Stay keys must be blanked on OTP — Shopify keeps omitted line properties
  var STAY_PROP_KEYS = [
    '_recurring',
    'Subscription',
    'Interval Unit',
    'Interval Frequency',
    'Recurring Discount Amount',
    'Discount Amount'
  ];

  function esc(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function cleanProperties(properties) {
    if (!properties || typeof properties !== 'object') return {};
    var skip = {
      _recurring: true,
      Subscription: true,
      'Interval Unit': true,
      'Interval Frequency': true,
      'Recurring Discount Amount': true,
      'Discount Amount': true
    };
    var out = {};
    Object.keys(properties).forEach(function (key) {
      if (skip[key]) return;
      var val = properties[key];
      if (val === null || val === undefined || val === '') return;
      out[key] = val;
    });
    return out;
  }

  function propertiesForSellingPlanChange(properties, clearingPlan) {
    var out = cleanProperties(properties);
    if (clearingPlan) {
      STAY_PROP_KEYS.forEach(function (key) {
        out[key] = '';
      });
    }
    return out;
  }

  class CartDeliveryFrequency {
    constructor(root) {
      this.root = root;
      this.busy = false;
      this.catalog = { lineItems: [] };
      this._bound = false;
      this._quietUntil = 0;
    }

    init() {
      if (!this.root) return;
      this._bind();
      this.refresh();
    }

    _isQuiet() {
      return Date.now() < (this._quietUntil || 0);
    }

    async refresh() {
      if (!this.root || this.busy) return;

      // Quiet period skips heavy catalog work, but consent must still mirror live cart
      if (this._isQuiet()) {
        await this._syncConsentFromLiveCart();
        return;
      }

      try {
        var pair = await Promise.all([
          this._json('/cart.js').catch(function () { return null; }),
          this._fetchCatalog().catch(function () { return null; })
        ]);
        this._liveCart = pair[0];
        var data = pair[1];
        this.catalog = data || { lineItems: [] };
        if (!this.catalog.lineItems || !this.catalog.lineItems.length) {
          // Fallback: build from per-line Edit UI that Rivets already injected
          this.catalog = this._catalogFromDom();
        }
        this._paintFromCatalog();
      } catch (err) {
        console.warn('[CartDeliveryFrequency] catalog fetch failed, using DOM', err);
        this.catalog = this._catalogFromDom();
        if (!this.catalog.lineItems.length) this._loadEmbeddedPayload();
        try {
          this._liveCart = await this._json('/cart.js');
        } catch (e2) {
          /* ignore */
        }
        this._paintFromCatalog();
      }
    }

    /** Lightweight consent sync — safe during quiet / partial UI inject. */
    async _syncConsentFromLiveCart() {
      if (!this.root) return;
      try {
        this._liveCart = await this._json('/cart.js');
      } catch (e) {
        /* keep previous */
      }
      var globalEl = this._ensureMount();
      if (!globalEl) return;
      var items = (this.catalog && this.catalog.lineItems) || this._catalogFromDom().lineItems || [];
      if (!globalEl.querySelector('[data-gs-enable]')) this._ensureShell(globalEl);

      var anySub = this._detectAnySubscription(items, this._liveCart);
      // Consent only appears when at least one line is subscribed
      if (!anySub) {
        globalEl.hidden = true;
        globalEl.style.display = 'none';
        globalEl.classList.remove('is-subscribed');
        var checkboxOff = globalEl.querySelector('[data-gs-enable]');
        if (checkboxOff) checkboxOff.checked = false;
        return;
      }

      globalEl.hidden = false;
      globalEl.style.display = '';
      globalEl.classList.add('is-subscribed');
      var checkbox = globalEl.querySelector('[data-gs-enable]');
      if (checkbox) checkbox.checked = true;
    }

    /**
     * Consent shows only when at least one line has a native selling plan.
     * Ignore leftover Stay props (_recurring) — they can linger after OTP.
     */
    _detectAnySubscription(items, cart) {
      if (cart && cart.items && cart.items.length) {
        for (var i = 0; i < cart.items.length; i++) {
          if (hasNativeSellingPlan(cart.items[i])) return true;
        }
      }

      if (items && items.length) {
        for (var j = 0; j < items.length; j++) {
          if (items[j] && items[j].sellingPlanId) return true;
        }
      }

      if (!this.root) return false;
      var uis = this.root.querySelectorAll('[data-gs-line-ui]');
      for (var k = 0; k < uis.length; k++) {
        var ui = uis[k];
        var selected = ui.querySelector('[data-gs-plan].is-selected');
        if (selected) {
          var pid = selected.getAttribute('data-gs-plan');
          if (pid) return true;
        }
      }
      return false;
    }

    /** Build a mini-catalog from injected [data-gs-line-ui] menus. */
    _catalogFromDom() {
      var lineItems = [];
      var discount = null;
      this.root.querySelectorAll('[data-gs-line-ui]').forEach(function (ui) {
        var key = ui.getAttribute('data-gs-line-key');
        if (!key) return;
        var plans = [];
        ui.querySelectorAll('[data-gs-plan]').forEach(function (btn) {
          var id = btn.getAttribute('data-gs-plan');
          if (id === '' || id == null) return;
          plans.push({
            id: String(id).trim(),
            name: (btn.textContent || '').trim()
          });
        });
        if (!plans.length) return;
        var selected = ui.querySelector('[data-gs-plan].is-selected');
        var sellingPlanId = null;
        var sellingPlanName = '';
        if (selected) {
          var sid = selected.getAttribute('data-gs-plan');
          if (sid) {
            sellingPlanId = String(sid).trim();
            sellingPlanName = (selected.textContent || '').trim();
          }
        }
        // If Edit UI says Subscription but is-selected is missing/OTP, infer from title/freq
        if (!sellingPlanId) {
          var titleEl = ui.querySelector('.gs-line__title');
          var freqEl = ui.querySelector('.gs-line__freq');
          var titleText = ((titleEl && titleEl.textContent) || '').trim();
          var freqText = ((freqEl && freqEl.textContent) || '').trim();
          if (/subscription/i.test(titleText) && freqText && !/one\s*time/i.test(freqText)) {
            for (var p = 0; p < plans.length; p++) {
              if (
                plans[p].name &&
                plans[p].name.toLowerCase().indexOf(freqText.toLowerCase()) !== -1
              ) {
                sellingPlanId = String(plans[p].id);
                sellingPlanName = plans[p].name;
                break;
              }
            }
            if (!sellingPlanId && plans[0]) {
              sellingPlanId = String(plans[0].id);
              sellingPlanName = plans[0].name || freqText;
            }
          }
        }
        lineItems.push({
          key: key,
          variantId: ui.getAttribute('data-gs-variant-id'),
          quantity: Number(ui.getAttribute('data-gs-quantity')) || 1,
          sellingPlanId: sellingPlanId,
          sellingPlanName: sellingPlanName,
          discount: discount,
          plans: plans,
          properties: {}
        });
      });
      return { lineItems: lineItems };
    }

    _loadEmbeddedPayload() {
      var el = this.root.querySelector('[data-line-items-json]');
      if (!el) return;
      try {
        this.catalog = JSON.parse(el.textContent);
      } catch (e) {
        this.catalog = { lineItems: [] };
      }
    }

    async _fetchCatalog() {
      var url = CATALOG_URL + (CATALOG_URL.indexOf('?') === -1 ? '?' : '&') + '_=' + Date.now();
      var res = await fetch(url, {
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { Accept: 'application/json' }
      });
      if (!res.ok) throw new Error('Catalog HTTP ' + res.status);
      var text = await res.text();
      // Theme view may wrap whitespace / HTML comments — extract JSON object
      var start = text.indexOf('{');
      var end = text.lastIndexOf('}');
      if (start === -1 || end === -1) throw new Error('Invalid catalog JSON');
      return JSON.parse(text.slice(start, end + 1));
    }

    _bind() {
      if (this._bound) return;
      this._bound = true;
      var self = this;

      this.root.addEventListener('change', function (e) {
        var t = e.target;
        if (t.matches && t.matches('[data-gs-enable]')) {
          self._onToggle(t.checked);
        }
        // Global frequency select removed — cadence is changed via per-line Edit only
      });

      this.root.addEventListener('click', function (e) {
        var editBtn = e.target.closest('[data-gs-edit]');
        if (editBtn) {
          e.preventDefault();
          e.stopPropagation();
          self._toggleLineMenu(editBtn.getAttribute('data-gs-edit'));
          return;
        }

        var planBtn = e.target.closest('[data-gs-plan]');
        if (planBtn && planBtn.hasAttribute('data-gs-line')) {
          e.preventDefault();
          e.stopPropagation();
          var lineKey = planBtn.getAttribute('data-gs-line');
          var planId = planBtn.getAttribute('data-gs-plan');
          // Close menu immediately for feedback
          var menu = planBtn.closest('[data-gs-menu]');
          if (menu) menu.hidden = true;
          self._applyLinePlan(lineKey, planId === '' || planId == null ? null : String(planId).trim());
          return;
        }

        if (!e.target.closest('[data-gs-line-ui]')) {
          self.root.querySelectorAll('[data-gs-menu]').forEach(function (m) {
            m.hidden = true;
          });
        }
      });

      if ($ && $(document).on) {
        $(document).on('cart.requestComplete', function () {
          // Rivets loads /cart?view=subscription async — wait for line Edit UI
          setTimeout(function () {
            if (!self.busy && !self._isQuiet()) self.refresh();
          }, 700);
          setTimeout(function () {
            if (!self.busy && !self._isQuiet()) self.refresh();
          }, 1400);
        });
      }

      // When Rivets injects per-line Edit UI, rebuild the cart-level checkbox
      if (typeof MutationObserver !== 'undefined') {
        var details = this.root.querySelector('.cart-details') || this.root;
        var injectTimer = null;
        var rowObserver = new MutationObserver(function () {
          clearTimeout(injectTimer);
          injectTimer = setTimeout(function () {
            if (!self.busy && !self._isQuiet()) self.refresh();
          }, 200);
        });
        rowObserver.observe(details, { childList: true, subtree: true });
      }

      // Refresh whenever the mini-cart is opened
      var wrapper = document.querySelector('.mini-cart-wrapper');
      if (wrapper && typeof MutationObserver !== 'undefined') {
        var openObserver = new MutationObserver(function () {
          var open =
            wrapper.style.display !== 'none' &&
            window.getComputedStyle(wrapper).display !== 'none';
          if (open && !self.busy && !self._isQuiet()) self.refresh();
        });
        openObserver.observe(wrapper, { attributes: true, attributeFilter: ['style', 'class'] });
      }

      if (typeof global.toggleCart === 'function' && !global.toggleCart.__gsWrapped) {
        var originalToggle = global.toggleCart;
        global.toggleCart = function () {
          originalToggle.apply(this, arguments);
          setTimeout(function () {
            if (!self.busy && !self._isQuiet()) self.refresh();
          }, 350);
        };
        global.toggleCart.__gsWrapped = true;
      }
    }

    _paintFromCatalog() {
      var globalEl = this._ensureMount();
      if (!globalEl) return;

      var items = (this.catalog && this.catalog.lineItems) || [];
      if (!globalEl.querySelector('[data-gs-enable]')) {
        this._ensureShell(globalEl);
      }

      // Consent only when at least one line is subscribed (auto-checked).
      // Hidden for one-time purchase only carts. Frequency stays on per-line Edit.
      var anySub = this._detectAnySubscription(items, this._liveCart);
      if (!anySub) {
        globalEl.hidden = true;
        globalEl.style.display = 'none';
        globalEl.classList.remove('is-subscribed');
        var checkboxHidden = globalEl.querySelector('[data-gs-enable]');
        if (checkboxHidden) checkboxHidden.checked = false;
        return;
      }

      globalEl.hidden = false;
      globalEl.style.display = '';
      globalEl.classList.add('is-subscribed');

      var discount = null;
      items.forEach(function (i) {
        if (discount == null && i.discount != null) discount = i.discount;
      });

      var checkbox = globalEl.querySelector('[data-gs-enable]');
      var badge = globalEl.querySelector('[data-gs-badge]');
      var success = globalEl.querySelector('[data-gs-success]');

      if (checkbox) checkbox.checked = true;

      if (badge) {
        if (discount != null && discount !== '') {
          badge.textContent = 'Save ' + discount + '%';
          badge.hidden = false;
        } else {
          badge.hidden = true;
        }
      }

      if (success && discount != null) {
        success.textContent =
          "You're subscribed" + (discount ? ' and saving ' + discount + '%!' : '!');
      }

      var jsonEl = globalEl.querySelector('[data-line-items-json]');
      if (jsonEl) {
        jsonEl.textContent = JSON.stringify(this.catalog);
      }
    }

    /** Find or create the consent mount inside the cart footer. */
    _ensureMount() {
      var globalEl = this.root.querySelector('[data-gs-global]');
      if (globalEl) return globalEl;

      var footer =
        this.root.querySelector('.cart-footer .totals-section') ||
        this.root.querySelector('.cart-footer');
      if (!footer) return null;

      globalEl = document.createElement('div');
      globalEl.className = 'gs-global gs-global--consent-only';
      globalEl.setAttribute('data-gs-global', '');
      globalEl.hidden = true;
      this._ensureShell(globalEl);

      var checkout = footer.querySelector('.checkout');
      var subtotal = footer.querySelector('.subtotal-lines');
      if (subtotal && subtotal.parentNode === footer) {
        footer.insertBefore(globalEl, subtotal);
      } else if (checkout) {
        footer.insertBefore(globalEl, checkout);
      } else {
        footer.appendChild(globalEl);
      }
      return globalEl;
    }

    _ensureShell(globalEl) {
      // Checkbox-only consent shell (no cart-level frequency dropdown)
      globalEl.innerHTML =
        '<label class="gs-global__toggle">' +
        '<input type="checkbox" data-gs-enable />' +
        '<span class="gs-global__label">Subscription consent confirmed</span>' +
        '<span class="gs-global__badge" data-gs-badge hidden></span>' +
        '</label>' +
        '<div class="gs-toast gs-toast--success" data-gs-success hidden role="status"></div>' +
        '<div class="gs-toast gs-toast--error" data-gs-error hidden role="status"></div>';
      var jsonEl = document.createElement('script');
      jsonEl.type = 'application/json';
      jsonEl.setAttribute('data-line-items-json', '');
      jsonEl.textContent = '{"lineItems":[]}';
      globalEl.appendChild(jsonEl);
    }

    /**
     * Build dropdown options from the UNION of Stay.ai plans on all eligible lines.
     * Keys are normalized ("weeks:4") so "4 Weeks" and "Deliver Every 4 Weeks" collapse.
     * Applying a key only updates lines that have a matching plan.
     */
    _collectCadences(items) {
      if (!items.length) return [];
      var map = {};
      items.forEach(function (item) {
        (item.plans || []).forEach(function (p) {
          var key = CartDeliveryFrequency._cadenceKey(p.name);
          if (!key) return;
          if (!map[key]) {
            map[key] = CartDeliveryFrequency._cadenceLabel(p.name, key);
          }
        });
      });
      return Object.keys(map)
        .sort(function (a, b) {
          var na = parseInt((a.match(/\d+/) || [])[0], 10);
          var nb = parseInt((b.match(/\d+/) || [])[0], 10);
          if (!isNaN(na) && !isNaN(nb) && na !== nb) return na - nb;
          return a.localeCompare(b);
        })
        .map(function (key) {
          return { key: key, label: map[key] };
        });
    }

    /** Normalize a Stay.ai plan name to a stable cadence key. */
    static _cadenceKey(name) {
      var s = String(name || '')
        .trim()
        .toLowerCase();
      if (!s) return '';
      var weeks = s.match(/(\d+)\s*-?\s*weeks?/);
      if (weeks) return 'weeks:' + weeks[1];
      var days = s.match(/(\d+)\s*-?\s*days?/);
      if (days) return 'days:' + days[1];
      var months = s.match(/(\d+)\s*-?\s*months?/);
      if (months) return 'months:' + months[1];
      return 'name:' + s;
    }

    static _cadenceLabel(name, key) {
      var m = String(key || '').match(/^(weeks|days|months):(\d+)$/);
      if (m) {
        var unit = m[1] === 'weeks' ? 'Weeks' : m[1] === 'days' ? 'Days' : 'Months';
        return m[2] + ' ' + unit;
      }
      return String(name || '').trim();
    }

    async _onToggle(enabled) {
      var self = this;

      if (!enabled) {
        // Uncheck = clear all selling plans → one-time purchase
        await this._run(async function () {
          await self._applyCadenceToAll(null);
          self._showSuccess(false);
          self._showError(false);
        });
        return;
      }

      // Checking does not force-subscribe. Frequency / subscribe is set via product Edit.
      // Snap checkbox back to cart state (checked only if at least one line is subscribed).
      this.refresh();
    }

    /**
     * Apply a cadence (plan name / weeks:N key) or one-time (null) to eligible lines.
     * Sequential /cart/change.js (Shopify cart writes are not safely parallel),
     * then in-place UI sync — no Liquid view reloads.
     * @param {string|null} cadenceName
     * @returns {Promise<{updated: number, alreadyCurrent: boolean}>}
     */
    async _applyCadenceToAll(cadenceName) {
      var empty = { updated: 0, alreadyCurrent: false };
      var items = (this._catalogFromDom().lineItems || []).slice();
      if (!items.length) {
        try {
          var fetched = await this._fetchCatalog();
          items = (fetched && fetched.lineItems) || [];
        } catch (e) {
          items = [];
        }
      }
      if (!items.length) return empty;

      // Jobs keyed by variant so we can rematch after each key-changing plan update
      var jobs = [];
      items.forEach(function (item) {
        var planId = null;
        var planName = '';
        if (cadenceName) {
          var plan = CartDeliveryFrequency._findPlanByName(item.plans || [], cadenceName);
          if (!plan) return;
          planId = String(plan.id);
          planName = String(plan.name || '');
        }
        jobs.push({
          variantId: String(item.variantId),
          planId: planId,
          planName: planName,
          quantity: Number(item.quantity) || 1,
          plans: item.plans || []
        });
      });
      if (!jobs.length) return empty;

      var cart = await this._json('/cart.js');
      var pending = [];
      var matchedLive = 0;

      for (var j = 0; j < jobs.length; j++) {
        var job = jobs[j];
        var live = CartDeliveryFrequency._findLiveLine(cart, null, job.variantId);
        if (!live) continue;
        matchedLive += 1;

        var currentId =
          live.selling_plan_allocation && live.selling_plan_allocation.selling_plan
            ? String(live.selling_plan_allocation.selling_plan.id)
            : null;
        var desiredId = job.planId ? String(job.planId) : null;
        if (currentId === desiredId) continue;

        pending.push({
          variantId: job.variantId,
          planId: job.planId,
          planName: job.planName,
          key: live.key,
          quantity: Number(live.quantity || job.quantity) || 1,
          planValue: desiredId ? Number(desiredId) || desiredId : ''
        });
      }

      // Every matching line already has this cadence / one-time — not an error
      if (!pending.length) {
        return { updated: 0, alreadyCurrent: matchedLive > 0 };
      }

      // Instant feedback while Shopify writes
      this._paintLinesOptimistic(pending, cadenceName);

      var updated = 0;
      for (var p = 0; p < pending.length; p++) {
        var req = pending[p];
        // Rematch by variant — keys change after each selling_plan write
        live = CartDeliveryFrequency._findLiveLine(cart, req.key, req.variantId);
        if (!live) continue;

        try {
          var clearingAll = !req.planValue;
          cart = await this._post('/cart/change.js', {
            id: live.key,
            quantity: Number(live.quantity || req.quantity) || 1,
            selling_plan: req.planValue,
            properties: propertiesForSellingPlanChange(live.properties || {}, clearingAll)
          });
          updated += 1;
        } catch (err) {
          console.warn('[CartDeliveryFrequency] change failed', req.variantId, err);
        }
      }

      if (!updated) return empty;

      this._finishCartWriteFast(cart);
      return { updated: updated, alreadyCurrent: false };
    }

    /**
     * Optimistic per-line labels before /cart/change.js completes.
     */
    _paintLinesOptimistic(jobs, cadenceName) {
      var self = this;
      (jobs || []).forEach(function (job) {
        var label = job.planName;
        if (!job.planId) {
          label = 'One time purchase';
        } else if (!label && cadenceName) {
          label = CartDeliveryFrequency._cadenceLabel(cadenceName, cadenceName);
        }
        self._paintOneLineUi(job.variantId, null, job.planId, label);
      });
    }

    /**
     * Update one line's Edit UI without a Liquid reload.
     */
    _paintOneLineUi(variantId, newKey, planId, planLabel) {
      var uis = this.root.querySelectorAll(
        '[data-gs-line-ui][data-gs-variant-id="' + String(variantId) + '"]'
      );
      Array.prototype.forEach.call(uis, function (ui) {
        if (newKey) {
          ui.setAttribute('data-gs-line-key', newKey);
          ui.querySelectorAll('[data-gs-edit]').forEach(function (b) {
            b.setAttribute('data-gs-edit', newKey);
          });
          ui.querySelectorAll('[data-gs-line]').forEach(function (b) {
            b.setAttribute('data-gs-line', newKey);
          });
        }

        var title = ui.querySelector('.gs-line__title');
        var freq = ui.querySelector('.gs-line__freq');
        var isSub = !!(planId && String(planId));
        if (title) title.textContent = isSub ? 'Subscription' : 'Purchase';
        if (freq) freq.textContent = planLabel || (isSub ? '' : 'One time purchase');

        ui.querySelectorAll('[data-gs-plan]').forEach(function (btn) {
          var id = btn.getAttribute('data-gs-plan');
          var selected =
            isSub ? String(id) === String(planId) : id === '' || id == null;
          btn.classList.toggle('is-selected', selected);
        });
      });
    }

    /** Paint all line Edit UIs from a live cart.js payload. */
    _paintLinesFromCart(cart) {
      var self = this;
      ((cart && cart.items) || []).forEach(function (item) {
        var plan =
          item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
            ? item.selling_plan_allocation.selling_plan
            : null;
        var planId = plan ? String(plan.id) : null;
        var label = plan ? String(plan.name || '') : 'One time purchase';
        self._paintOneLineUi(item.variant_id, item.key, planId, label);
      });
    }

    /**
     * After writes: sync CartJS in-place (keeps Edit DOM), paint UI, skip Liquid views.
     */
    _finishCartWriteFast(cart) {
      this._quietUntil = Date.now() + 3000;
      this._liveCart = cart;
      this._syncCartJsInPlace(cart);
      this._paintLinesFromCart(cart);
      this.catalog = this._catalogFromDom();
      this._paintFromCatalog();
      // Recompute "You're Saving" after selling_plan / price sync
      try {
        if (typeof global.updateCartDiscounts === 'function') {
          global.updateCartDiscounts();
          setTimeout(function () {
            try {
              global.updateCartDiscounts();
            } catch (e2) {
              /* ignore */
            }
          }, 50);
        }
      } catch (e) {
        /* ignore */
      }
    }

    /**
     * Match a plan for a cadence key or display name.
     * Supports normalized keys from the dropdown: "weeks:4", "days:30", etc.
     */
    static _findPlanByName(plans, cadenceName) {
      var want = String(cadenceName || '').trim();
      if (!want) return null;

      // Normalized key from cart-level dropdown (weeks:4)
      var keyed = want.match(/^(weeks|days|months):(\d+)$/i);
      if (keyed) {
        var unit = keyed[1].toLowerCase();
        var num = keyed[2];
        var unitRe =
          unit === 'weeks'
            ? new RegExp(num + '\\s*-?\\s*weeks?', 'i')
            : unit === 'days'
              ? new RegExp(num + '\\s*-?\\s*days?', 'i')
              : new RegExp(num + '\\s*-?\\s*months?', 'i');
        return (
          plans.find(function (p) {
            return unitRe.test(String(p.name || ''));
          }) || null
        );
      }

      var wantLower = want.toLowerCase();
      var exact = plans.find(function (p) {
        return String(p.name || '').trim().toLowerCase() === wantLower;
      });
      if (exact) return exact;

      var wantKey = CartDeliveryFrequency._cadenceKey(want);
      if (wantKey) {
        var byKey = plans.find(function (p) {
          return CartDeliveryFrequency._cadenceKey(p.name) === wantKey;
        });
        if (byKey) return byKey;
      }

      return (
        plans.find(function (p) {
          var name = String(p.name || '').trim().toLowerCase();
          return name.indexOf(wantLower) !== -1 || wantLower.indexOf(name) !== -1;
        }) || null
      );
    }

    /** Find a live cart line by key, falling back to variant id. */
    static _findLiveLine(cart, key, variantId) {
      var items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) {
        if (items[i].key === key) return items[i];
      }
      if (variantId != null) {
        for (var j = 0; j < items.length; j++) {
          if (String(items[j].variant_id) === String(variantId)) return items[j];
        }
      }
      return null;
    }

    async _applyLinePlan(lineKey, planId) {
      var self = this;
      await this._run(async function () {
        var cart = await self._json('/cart.js');
        var live = null;
        for (var i = 0; i < (cart.items || []).length; i++) {
          if (cart.items[i].key === lineKey) {
            live = cart.items[i];
            break;
          }
        }
        if (!live) throw new Error('Cart line not found: ' + lineKey);

        // Optimistic UI for this line
        var label = 'One time purchase';
        if (planId) {
          var ui = self.root.querySelector(
            '[data-gs-line-ui][data-gs-line-key="' + lineKey + '"]'
          );
          var btn =
            ui &&
            ui.querySelector('[data-gs-plan="' + String(planId) + '"]');
          label = btn ? (btn.textContent || '').trim() : label;
        }
        self._paintOneLineUi(live.variant_id, live.key, planId, label);

        cart = await self._changeLineSellingPlan(
          {
            key: live.key,
            variantId: live.variant_id,
            quantity: live.quantity,
            properties: live.properties || {}
          },
          planId
        );

        // change.js returns the cart; fall back if older path returned something else
        if (!cart || !cart.items) {
          cart = await self._json('/cart.js');
        }
        self._finishCartWriteFast(cart);
      });
    }

    async _changeLineSellingPlan(item, sellingPlanId) {
      var qty = Number(item.quantity) || 1;
      var clearing =
        sellingPlanId === null || sellingPlanId === '' || sellingPlanId === undefined;
      var planValue = clearing ? '' : Number(sellingPlanId) || sellingPlanId;
      var props = propertiesForSellingPlanChange(item.properties || {}, clearing);

      // Prefer line-item key — Shopify accepts `id` as the key string
      var bodyByKey = {
        id: item.key,
        quantity: qty,
        selling_plan: planValue,
        properties: props
      };

      try {
        return await this._post('/cart/change.js', bodyByKey);
      } catch (errKey) {
        console.warn('[CartDeliveryFrequency] change by key failed, trying line index', errKey);
      }

      // Fallback: resolve 1-based line index once
      var cart = await this._json('/cart.js');
      var live = null;
      var line = null;
      for (var i = 0; i < (cart.items || []).length; i++) {
        if (cart.items[i].key === item.key || String(cart.items[i].variant_id) === String(item.variantId)) {
          live = cart.items[i];
          line = i + 1;
          break;
        }
      }
      if (!live || !line) throw new Error('Line not in cart');

      try {
        return await this._post('/cart/change.js', {
          line: line,
          quantity: Number(live.quantity) || qty,
          selling_plan: planValue,
          properties: propertiesForSellingPlanChange(
            live.properties || item.properties || {},
            clearing
          )
        });
      } catch (errLine) {
        // NEVER blind remove+add: two non-atomic writes racing the gift sync /
        // Rebuy left both the old and the new line in the cart (duplicate
        // subscription rows). Instead re-fetch and retry the in-place change
        // with a fresh key; only add if the line is truly gone from the cart.
        console.warn('[CartDeliveryFrequency] change.js by line failed, refetch+retry', errLine);
        var freshCart = await this._json('/cart.js');
        var fresh = CartDeliveryFrequency._findLiveLine(
          freshCart,
          item.key,
          item.variantId || live.variant_id
        );
        if (fresh) {
          return await this._post('/cart/change.js', {
            id: fresh.key,
            quantity: Number(fresh.quantity) || qty,
            selling_plan: planValue,
            properties: propertiesForSellingPlanChange(
              fresh.properties || item.properties || {},
              clearing
            )
          });
        }
        var addBody = {
          items: [
            {
              id: Number(item.variantId || live.variant_id),
              quantity: Number(live.quantity) || qty
            }
          ]
        };
        if (planValue !== '') {
          addBody.items[0].selling_plan = planValue;
        }
        var addProps = cleanProperties(item.properties || live.properties);
        if (Object.keys(addProps).length) addBody.items[0].properties = addProps;
        await this._post('/cart/add.js', addBody);
        return this._json('/cart.js');
      }
    }

    /** Re-inject per-line Edit UI from cart?view=subscription (fallback only). */
    async _reloadSubscriptionRows() {
      try {
        var res = await fetch('/cart?view=subscription', { credentials: 'same-origin' });
        var html = await res.text();
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var cart = await this._json('/cart.js');
        (cart.items || []).forEach(function (v) {
          var wrap =
            doc.querySelector('.subscription-wrapper[datakey="' + v.key + '"]') ||
            doc.querySelector('.subscription-wrapper[data-variant-id="' + v.variant_id + '"]');
          if (!wrap) {
            doc.querySelectorAll('.subscription-wrapper').forEach(function (w) {
              var ui = w.querySelector('[data-gs-variant-id="' + v.variant_id + '"]');
              if (ui) wrap = w;
            });
          }
          var slots = document.querySelectorAll(
            '.line-item[datakey="' +
              v.key +
              '"] .appen_data, .line-item[data-variant-id="' +
              v.variant_id +
              '"] .appen_data'
          );
          if (!slots.length) {
            slots = document.querySelectorAll('.line-item[datakey="' + v.key + '"] .appen_data');
          }
          var inner = wrap ? wrap.innerHTML : '';
          if (inner && v.key) {
            inner = inner
              .replace(/data-gs-line-key="[^"]*"/g, 'data-gs-line-key="' + v.key + '"')
              .replace(/data-gs-line="[^"]*"/g, 'data-gs-line="' + v.key + '"')
              .replace(/data-gs-edit="[^"]*"/g, 'data-gs-edit="' + v.key + '"');
          }
          Array.prototype.forEach.call(slots, function (slot) {
            slot.innerHTML = inner;
          });
        });
      } catch (e) {
        console.warn('[CartDeliveryFrequency] reload subscription rows failed', e);
      }
    }

    /**
     * Update CartJS without replacing the items array (avoids wiping .appen_data).
     */
    _syncCartJsInPlace(cart) {
      if (!cart || !global.CartJS || !CartJS.cart) return;
      try {
        var c = CartJS.cart;
        Object.keys(cart).forEach(function (key) {
          if (key === 'items') return;
          c[key] = cart[key];
        });

        var remote = cart.items || [];
        var local = c.items || [];
        if (local.length === remote.length && local.length) {
          for (var i = 0; i < remote.length; i++) {
            if (local[i] && typeof local[i].update === 'function') {
              local[i].update(remote[i]);
            } else if (local[i]) {
              Object.keys(remote[i]).forEach(function (k) {
                if (k === 'properties') return;
                local[i][k] = remote[i][k];
              });
              local[i].properties = remote[i].properties || {};
            }
          }
        } else if (typeof c.update === 'function') {
          // Line count changed — full rebuild is required
          c.update(cart);
        }
      } catch (e) {
        console.warn('[CartDeliveryFrequency] in-place CartJS sync failed', e);
      }
    }

    async _syncCartJs() {
      try {
        var cart = await this._json('/cart.js');
        this._syncCartJsInPlace(cart);
        if (typeof global.updateCartDiscounts === 'function') {
          global.updateCartDiscounts();
        }
      } catch (e) {
        console.warn('[CartDeliveryFrequency] CartJS sync failed', e);
      }
    }

    _toggleLineMenu(key) {
      this.root.querySelectorAll('[data-gs-line-ui]').forEach(function (ui) {
        var menu = ui.querySelector('[data-gs-menu]');
        if (!menu) return;
        if (ui.getAttribute('data-gs-line-key') === key) {
          menu.hidden = !menu.hidden;
        } else {
          menu.hidden = true;
        }
      });
    }

    async _run(fn) {
      // Queue instead of silently dropping clicks while a prior update runs
      if (this.busy) {
        this._queued = fn;
        return;
      }
      this._setBusy(true);
      this._showError(false);
      try {
        await fn();
      } catch (err) {
        console.error('[CartDeliveryFrequency]', err);
        this._showError(true);
        try {
          await this.refresh();
        } catch (e2) {
          /* ignore */
        }
      } finally {
        this._setBusy(false);
        if (this._queued) {
          var next = this._queued;
          this._queued = null;
          this._run(next);
        }
      }
    }

    _setBusy(busy) {
      this.busy = !!busy;
      this.root.classList.toggle('gs-is-loading', this.busy);
      this.root
        .querySelectorAll('[data-gs-enable], [data-gs-frequency], [data-gs-edit], [data-gs-plan]')
        .forEach(function (el) {
          el.disabled = !!busy;
        });
    }

    _showSuccess(show) {
      var el = this.root.querySelector('[data-gs-success]');
      if (!el) return;
      el.hidden = !show;
      if (show) {
        clearTimeout(this._successTimer);
        this._successTimer = setTimeout(function () {
          el.hidden = true;
        }, 2800);
      }
    }

    _showError(show) {
      var el = this.root.querySelector('[data-gs-error]');
      if (!el) return;
      el.hidden = !show;
    }

    _json(url) {
      var bust = url + (url.indexOf('?') === -1 ? '?' : '&') + '_=' + Date.now();
      return fetch(bust, {
        credentials: 'same-origin',
        cache: 'no-store'
      }).then(function (r) {
        if (!r.ok) throw new Error(url + ' ' + r.status);
        return r.json();
      });
    }

    _post(url, body) {
      return fetch(url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify(body)
      }).then(function (r) {
        if (!r.ok) {
          return r.json().catch(function () {
            return {};
          }).then(function (data) {
            throw new Error((data && data.description) || url + ' ' + r.status);
          });
        }
        return r.json();
      });
    }
  }

  function boot() {
    var root = document.querySelector(ROOT_SEL);
    if (!root) return;
    if (global.__joiCartDeliveryFrequency) {
      global.__joiCartDeliveryFrequency.refresh();
      return;
    }
    var instance = new CartDeliveryFrequency(root);
    global.__joiCartDeliveryFrequency = instance;
    instance.init();
  }

  if ($ && typeof $.fn !== 'undefined') {
    $(function () {
      setTimeout(boot, 80);
    });
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      setTimeout(boot, 80);
    });
  } else {
    setTimeout(boot, 80);
  }

  window.addEventListener('load', function () {
    setTimeout(function () {
      if (!global.__joiCartDeliveryFrequency) boot();
      else global.__joiCartDeliveryFrequency.refresh();
    }, 350);
  });
})(typeof window !== 'undefined' ? window : this, typeof jQuery !== 'undefined' ? jQuery : null);
